import { DiscardProgressDialog } from "components/dialog/DiscardProgressDialog";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface GuardRegistration {
  isDirty: boolean;
  onDiscard: () => void;
}

interface NavigationGuardContextValue {
  /**
   * Wrap any navigation side effect. If a consumer has registered a dirty
   * flow, the discard dialog opens and `proceed` runs only after the user
   * confirms; otherwise `proceed` runs immediately.
   */
  requestNavigation: (proceed: () => void) => void;
  /** Register the current dirty state + discard callback for the active page. */
  register: (registration: GuardRegistration) => void;
}

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

/**
 * App-level gate for guarding in-progress flows against navigation triggered
 * outside the flow's own component tree (e.g. the header logo, account, and
 * logout actions). A page registers its dirty state via
 * {@link useRegisterNavigationGuard}; navigation sources call
 * {@link useNavigationGuard} and route through `requestNavigation`.
 *
 * This is an explicit opt-in gate, not a global router blocker: only
 * navigation that is wrapped in `requestNavigation` is intercepted. Raw
 * `<Link>` / `navigate` calls, browser back/forward, and reloads are not
 * covered.
 */
export const NavigationGuardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const guardRef = useRef<GuardRegistration>({
    isDirty: false,
    onDiscard: () => {},
  });
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(
    null,
  );

  const register = useCallback((registration: GuardRegistration) => {
    guardRef.current = registration;
  }, []);

  const requestNavigation = useCallback((proceed: () => void) => {
    if (guardRef.current.isDirty) {
      setPendingProceed(() => proceed);
    } else {
      proceed();
    }
  }, []);

  const handleConfirm = () => {
    guardRef.current.onDiscard();
    const proceed = pendingProceed;
    setPendingProceed(null);
    proceed?.();
  };

  const handleCancel = () => setPendingProceed(null);

  return (
    <NavigationGuardContext.Provider value={{ requestNavigation, register }}>
      {children}
      <DiscardProgressDialog
        open={pendingProceed !== null}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </NavigationGuardContext.Provider>
  );
};

/**
 * Accessor for the navigation gate. Returns a no-op `requestNavigation`
 * (runs `proceed` immediately) when used outside a provider, so navigation
 * sources never break when the guard is absent.
 */
export const useNavigationGuard = () => {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    return { requestNavigation: (proceed: () => void) => proceed() };
  }
  return { requestNavigation: context.requestNavigation };
};

/**
 * Registers the calling page's dirty state and discard callback with the
 * nearest {@link NavigationGuardProvider}. No-op outside a provider.
 */
export const useRegisterNavigationGuard = (
  isDirty: boolean,
  onDiscard: () => void,
) => {
  const context = useContext(NavigationGuardContext);
  const register = context?.register;
  useEffect(() => {
    if (!register) return;
    register({ isDirty, onDiscard });
    return () => register({ isDirty: false, onDiscard: () => {} });
  }, [isDirty, onDiscard, register]);
};
